import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AdminPhoneSettings } from "@/components/admin/settings/AdminPhoneSettings";
import { SignatureSettings } from "@/components/admin/settings/SignatureSettings";
import { CSSOverrideSettings } from "@/components/admin/settings/CSSOverrideSettings";
import { AdminProfileLink } from "@/components/admin/settings/AdminProfileLink";
import { FAQManager } from "@/components/admin/settings/FAQManager";
import { ScheduleSettings } from "@/components/admin/settings/ScheduleSettings";
import { QuotaSettings } from "@/components/admin/settings/QuotaSettings";
import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function SystemSettings() {
  const [quotesEnabled, setQuotesEnabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { toast } = useToast();

  // Fetch quote delivery enabled setting
  useEffect(() => {
    const fetchQuoteDeliveryEnabled = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('admin_settings')
          .select('setting_value')
          .eq('setting_key', 'quote_delivery_enabled')
          .single();

        if (error) {
          console.error("Error fetching quote delivery setting:", error);
          return;
        }

        if (data) {
          // Handle different data types
          if (typeof data.setting_value === 'boolean') {
            setQuotesEnabled(data.setting_value);
          } else if (typeof data.setting_value === 'string') {
            setQuotesEnabled(data.setting_value === 'true');
          } else {
            // Default to true if setting not found or invalid
            setQuotesEnabled(true);
          }
        }
      } catch (error) {
        console.error("Error fetching quote delivery setting:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuoteDeliveryEnabled();
  }, []);

  // Update quote delivery enabled setting
  const handleQuotesEnabledChange = async (enabled: boolean) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          setting_key: 'quote_delivery_enabled',
          setting_value: enabled,
        }, {
          onConflict: 'setting_key'
        });

      if (error) {
        throw error;
      }

      setQuotesEnabled(enabled);
      toast({
        title: "Settings Updated",
        description: `Quote delivery has been ${enabled ? 'enabled' : 'disabled'}.`
      });
    } catch (error) {
      console.error("Error updating quote delivery setting:", error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "Could not update quote delivery setting."
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">System Settings</h1>
      
      {/* Global Quotes Enabled Toggle */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quote Delivery</CardTitle>
          <CardDescription>Enable or disable quote delivery system-wide</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="quotes-enabled" className="flex flex-col gap-1">
              <span>Quotes Enabled</span>
              <span className="font-normal text-sm text-muted-foreground">
                When disabled, no quotes will be sent to subscribers
              </span>
            </Label>
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <Switch 
                id="quotes-enabled" 
                checked={quotesEnabled}
                disabled={isSaving}
                onCheckedChange={handleQuotesEnabledChange}
              />
            )}
          </div>
        </CardContent>
      </Card>
      
      <Tabs defaultValue="phone">
        <TabsList className="mb-6">
          <TabsTrigger value="phone">Phone</TabsTrigger>
          <TabsTrigger value="signatures">Signatures</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="quota">Quota</TabsTrigger>
          <TabsTrigger value="css">CSS Override</TabsTrigger>
          <TabsTrigger value="profile">Profile Link</TabsTrigger>
          <TabsTrigger value="faq">FAQ Manager</TabsTrigger>
        </TabsList>
        
        <TabsContent value="phone">
          <AdminPhoneSettings />
        </TabsContent>
        
        <TabsContent value="signatures">
          <SignatureSettings />
        </TabsContent>
        
        <TabsContent value="schedule">
          <ScheduleSettings />
        </TabsContent>
        
        <TabsContent value="quota">
          <QuotaSettings />
        </TabsContent>
        
        <TabsContent value="css">
          <CSSOverrideSettings />
        </TabsContent>
        
        <TabsContent value="profile">
          <AdminProfileLink />
        </TabsContent>
        
        <TabsContent value="faq">
          <FAQManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
